import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Image as ImageIcon } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { RoomPhotos, photoUrl } from "./RoomPhotos";

const STATUSES = ["Disponible", "Reservada", "Ocupada"];

export function RoomsAdmin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(null);
  const [photoRoom, setPhotoRoom] = useState(null);

  const load = () => api.get("/admin/rooms").then((r) => setRooms(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const setField = (id, field, value) =>
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const updateRoomPhotos = (roomId, photos) => {
    setRooms((prev) => prev.map((r) => (r.id === roomId ? { ...r, photos } : r)));
    setPhotoRoom((pr) => (pr && pr.id === roomId ? { ...pr, photos } : pr));
  };

  const save = async (room) => {
    setSaving(room.id);
    try {
      const payload = isSuper
        ? {
            number: room.number,
            size: room.size,
            price: room.price,
            status: room.status,
            active: room.active,
          }
        : { status: room.status };
      await api.put(`/admin/rooms/${room.id}`, payload);
      toast.success(`Habitación ${room.number} actualizada`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  const addRoom = async () => {
    try {
      await api.post("/admin/rooms", {
        number: "Nueva",
        size: "0 m²",
        price: "$0",
        status: "Disponible",
        active: true,
        image: "room",
      });
      toast.success("Habitación creada");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (room) => {
    if (!window.confirm(`¿Eliminar la habitación ${room.number}?`)) return;
    try {
      await api.delete(`/admin/rooms/${room.id}`);
      toast.success("Habitación eliminada");
      setRooms((prev) => prev.filter((r) => r.id !== room.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const cell = "border border-[#B2967D]/40 px-2 py-1.5 text-sm w-full bg-white focus:outline-none focus:border-[#7D5A44]";

  return (
    <div data-testid="admin-rooms">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-black">Habitaciones</h1>
          <p className="mt-2 text-sm font-light text-black/50">
            {isSuper
              ? "Edita número, m², precio, estatus, fotos y publicación. Los cambios se reflejan en el sitio público."
              : "Tu rol permite cambiar el estatus y ver las fotografías. Los demás campos son de solo lectura."}
          </p>
        </div>
        {isSuper && (
          <button
            onClick={addRoom}
            data-testid="add-room-btn"
            className="inline-flex items-center gap-2 bg-[#7D5A44] text-white px-5 py-3 text-xs font-bold uppercase tracking-wide hover:bg-black transition-colors"
          >
            <Plus size={16} /> Nueva habitación
          </button>
        )}
      </div>

      <div className="mt-8 bg-white border border-[#B2967D]/25 overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr className="bg-[#F5F1EA] text-left text-[11px] uppercase tracking-wide text-[#B2967D]">
              <th className="px-4 py-3 font-bold">No.</th>
              <th className="px-4 py-3 font-bold">m²</th>
              <th className="px-4 py-3 font-bold">Precio</th>
              <th className="px-4 py-3 font-bold">Estatus</th>
              <th className="px-4 py-3 font-bold">Fotos</th>
              <th className="px-4 py-3 font-bold">Publicada</th>
              <th className="px-4 py-3 font-bold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((room, i) => (
              <tr key={room.id} className="border-t border-[#B2967D]/20" data-testid={`admin-room-row-${i}`}>
                <td className="px-4 py-2 w-24">
                  {isSuper ? (
                    <input className={cell} value={room.number} onChange={(e) => setField(room.id, "number", e.target.value)} data-testid={`room-number-${i}`} />
                  ) : (
                    <span className="text-sm font-bold text-black" data-testid={`room-number-${i}`}>{room.number}</span>
                  )}
                </td>
                <td className="px-4 py-2 w-28">
                  {isSuper ? (
                    <input className={cell} value={room.size} onChange={(e) => setField(room.id, "size", e.target.value)} data-testid={`room-size-${i}`} />
                  ) : (
                    <span className="text-sm font-light text-black/70" data-testid={`room-size-${i}`}>{room.size}</span>
                  )}
                </td>
                <td className="px-4 py-2 w-28">
                  {isSuper ? (
                    <input className={cell} value={room.price} onChange={(e) => setField(room.id, "price", e.target.value)} data-testid={`room-price-${i}`} />
                  ) : (
                    <span className="text-sm font-light text-black/70" data-testid={`room-price-${i}`}>{room.price}</span>
                  )}
                </td>
                <td className="px-4 py-2 w-36">
                  <select className={cell} value={room.status} onChange={(e) => setField(room.id, "status", e.target.value)} data-testid={`room-status-select-${i}`}>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2 w-44">
                  <div className="flex items-center gap-2">
                    {room.photos && room.photos.length > 0 ? (
                      <img src={photoUrl(room.photos[0].id)} alt="principal" className="h-9 w-12 object-cover border border-[#B2967D]/30" />
                    ) : (
                      <span className="h-9 w-12 flex items-center justify-center border border-dashed border-[#B2967D]/40 text-[#B2967D]">
                        <ImageIcon size={14} />
                      </span>
                    )}
                    <button
                      onClick={() => setPhotoRoom(room)}
                      data-testid={`room-photos-btn-${i}`}
                      className="text-xs font-bold uppercase tracking-wide text-[#7D5A44] hover:underline"
                    >
                      {isSuper ? "Fotos" : "Ver fotos"} ({room.photos?.length || 0})
                    </button>
                  </div>
                </td>
                <td className="px-4 py-2 w-24 text-center">
                  {isSuper ? (
                    <input type="checkbox" checked={!!room.active} onChange={(e) => setField(room.id, "active", e.target.checked)} data-testid={`room-active-${i}`} className="h-4 w-4 accent-[#7D5A44]" />
                  ) : (
                    <span data-testid={`room-active-${i}`} className={`text-xs font-bold ${room.active ? "text-[#7D5A44]" : "text-[#B2967D]"}`}>
                      {room.active ? "Sí" : "No"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => save(room)}
                    disabled={saving === room.id}
                    data-testid={`room-save-${i}`}
                    className="inline-flex items-center gap-1 bg-[#7D5A44] text-white px-3 py-2 text-xs font-bold uppercase hover:bg-black transition-colors disabled:opacity-60"
                  >
                    <Save size={14} /> Guardar
                  </button>
                  {isSuper && (
                    <button
                      onClick={() => remove(room)}
                      data-testid={`room-delete-${i}`}
                      className="ml-2 inline-flex items-center gap-1 border border-[#B2967D]/50 text-black/60 px-3 py-2 text-xs hover:border-red-500 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {photoRoom && (
        <RoomPhotos room={photoRoom} canEdit={isSuper} onClose={() => setPhotoRoom(null)} onChange={updateRoomPhotos} />
      )}
    </div>
  );
}
